import { type OpRecord } from '@dl/protocol';

/**
 * Environment-agnostic read interface. The server implements this over
 * DOSQL inside the WorkspaceDO; the client implements it over the
 * SQLite-WASM replica via Comlink (or directly in node tests).
 *
 * The projection function never touches storage directly — it goes
 * through this surface. That keeps `project()` testable with in-memory
 * stores and keeps the chokepoint invariant clean.
 */
export interface ReadOnlyStore {
  /** All accepted ops in HLC order. */
  iterateOps(): Iterable<OpRecord<unknown>>;
  /**
   * Optional: the current Workspace canonical-day cursor. When the caller
   * passes `t === null` to `project()`, this resolves to "current canonical
   * now" (03-information-model.md §6). Implementations without a cursor
   * may omit; the projection treats absence as "no validity filter".
   *
   * The cursor itself lives in Phase 1 calendar work (II.C); this hook is
   * the substrate-side seam.
   */
  currentCanonicalDay?(): number | null;
  /**
   * Optional: the declared valid-time `uniqueness_key` for a base predicate
   * id (ADR-0005 §2). Returns the key array (`[]` = `∅`, `['object']` =
   * `{object}`, `[fld_…]` = tuple-field key) or `undefined` when the
   * predicate is NOT valid-time segmented (or no registry is available).
   *
   * The projection consults this to group segments correctly: `∅` competes
   * all segments of a `(subject, predicate)`; `{object}`/`{role}` sub-group
   * so distinct object values / roles coexist. When the accessor is absent,
   * the projection falls back to `∅`-style grouping for any predicate that
   * already shows segment structure (the S1 behaviour) — a store without a
   * registry still resolves single-key successions correctly.
   */
  predicateUniquenessKey?(predicate: string): readonly string[] | undefined;
}

/** Test/utility implementation that wraps an in-memory array of ops. */
export function memoryStore(
  ops: OpRecord<unknown>[],
  options: {
    currentCanonicalDay?: number | null;
    /**
     * Map of base-predicate id → declared `uniqueness_key` (ADR-0005 §2). When
     * provided, `project()` groups segments by the key (so `{object}`/`{role}`
     * predicates coexist by value/role). Omit for the S1 `∅`-only behaviour.
     */
    uniquenessKeys?: Record<string, readonly string[]>;
  } = {},
): ReadOnlyStore {
  const cursor = options.currentCanonicalDay ?? null;
  const keys = options.uniquenessKeys;
  return {
    iterateOps: () => ops,
    currentCanonicalDay: () => cursor,
    ...(keys
      ? { predicateUniquenessKey: (predicate: string) => keys[predicate] }
      : {}),
  };
}
